import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Check, Loader2, MapPin, Navigation, Phone } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { SITE } from "@/lib/site";
import { BookingDialog } from "./BookingDialog";

const usd = (n) => (n == null ? "" : `$${Math.round(n).toLocaleString("en-US")}`);

const Listings = ({ c }) => (
  <div className="flex flex-col gap-2" data-testid="concierge-card-listings">
    {(c.items ?? []).map((h) => (
      <Link key={h.listing_key} to={`/listing/${h.listing_key}`} data-testid={`concierge-listing-${h.listing_key}`}
        className="flex gap-3 rounded-lg border border-neutral-200 bg-white p-2 transition-colors hover:border-ink">
        {h.image && <img src={h.image} alt="" className="h-16 w-20 shrink-0 rounded object-cover" />}
        <div className="min-w-0 text-[13px]">
          <p className="font-medium text-ink">{usd(h.price)}</p>
          <p className="truncate text-neutral-600">{h.beds} bd · {h.baths} ba · {h.sqft?.toLocaleString?.("en-US")} sqft</p>
          <p className="truncate text-neutral-500">{h.city}{h.base_miles != null ? ` · ${h.base_miles} mi to base` : h.nearest_base ? ` · near ${h.nearest_base}` : ""}</p>
        </div>
      </Link>
    ))}
  </div>
);

const Places = ({ c }) => (
  <div className="rounded-lg border border-neutral-200 bg-white p-3" data-testid="concierge-card-places">
    <p className="mb-2 text-[12px] text-neutral-500">From {c.from}</p>
    <ul className="space-y-1.5">
      {(c.items ?? []).map((p, i) => (
        <li key={`${p.name}-${i}`} className="flex items-center justify-between gap-2 text-[13px]">
          <a href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer"
            className="flex min-w-0 items-center gap-1.5 text-ink hover:underline" data-testid={`concierge-place-${i}`}>
            <MapPin className="h-3.5 w-3.5 shrink-0 text-brass" /> <span className="truncate">{p.name}</span>
          </a>
          <span className="shrink-0 font-medium text-ink">{p.miles} mi</span>
        </li>
      ))}
    </ul>
  </div>
);

const Distance = ({ c }) => (
  <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 text-[13px]" data-testid="concierge-card-distance">
    <Navigation className="h-5 w-5 shrink-0 text-brass" />
    <p className="text-neutral-700"><span className="font-medium text-ink">{c.miles} mi</span> straight-line · {c.from} to {c.to}</p>
  </div>
);

const LinkCard = ({ c, onNavigate }) => (
  <Link to={c.to} onClick={onNavigate} data-testid="concierge-card-link"
    className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-neutral-800">
    {c.label} <ArrowRight className="h-3.5 w-3.5" />
  </Link>
);

/** Call the team now (tap-to-call) or leave a number for an immediate call back (lands in the admin inbox). */
export const CallCard = () => {
  const [f, setF] = useState({ first_name: "", phone: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const [bookingOpen, setBookingOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    setErr("");
    try {
      await api.post("/enquiries", { kind: "callback", topic: "ai-concierge", first_name: f.first_name, phone: f.phone, consent: true,
        message: `Call me now (requested from the AI concierge on ${window.location.pathname})` });
      setState("done");
    } catch (ex) {
      setErr(errorMessage(ex));
      setState("idle");
    }
  };
  return (
    <div className="rounded-lg bg-ink p-4 text-white" data-testid="concierge-card-call">
      <p className="text-[14px] font-medium">Talk to a real person now</p>
      <a href={SITE.phoneMainHref} data-testid="concierge-call-now"
        className="mt-3 flex items-center justify-center gap-2 rounded-full bg-brass py-3 text-[15px] font-medium text-ink transition-colors hover:bg-white">
        <Phone className="h-4 w-4" /> Call {SITE.phoneMain}
      </a>
      <button type="button" onClick={() => setBookingOpen(true)} data-testid="concierge-book-time"
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-white/40 py-2.5 text-[14px] text-white transition-colors hover:bg-white hover:text-ink">
        <CalendarDays className="h-4 w-4" /> Choose a time to talk
      </button>
      {state === "done" ? (
        <p className="mt-3 flex items-center gap-2 text-[13px] text-white/90" data-testid="concierge-callback-done"><Check className="h-4 w-4 text-brass" /> Got it! Rob's team will call you shortly.</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex flex-col gap-2" data-testid="concierge-callback-form">
          <p className="text-[12px] text-white/75">Or we'll call you back right away:</p>
          <div className="flex gap-2">
            <input required size={1} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} placeholder="Name" maxLength={80}
              className="h-10 min-w-0 flex-1 rounded-md bg-white/10 px-3 text-[16px] text-white placeholder:text-white/60 focus:outline-none focus:ring-1 focus:ring-brass" data-testid="concierge-callback-name" />
            <input required size={1} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" maxLength={40}
              className="h-10 min-w-0 flex-1 rounded-md bg-white/10 px-3 text-[16px] text-white placeholder:text-white/60 focus:outline-none focus:ring-1 focus:ring-brass" data-testid="concierge-callback-phone" />
          </div>
          <button type="submit" disabled={state === "busy"} data-testid="concierge-callback-submit"
            className="flex h-10 items-center justify-center gap-2 rounded-full border border-white/40 text-[13px] transition-colors hover:bg-white hover:text-ink disabled:opacity-60">
            {state === "busy" && <Loader2 className="h-4 w-4 animate-spin" />} Call me now
          </button>
          {err && <p className="text-[12px] text-red-300" data-testid="concierge-callback-error">{err}</p>}
          <p className="text-[11.5px] leading-snug text-white/65">By sending, you agree The Rob Resh Team may call you about real estate.</p>
        </form>
      )}
      {bookingOpen && <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} />}
    </div>
  );
};

export const ConciergeCards = ({ cards, onNavigate }) => (
  <div className="mt-2 flex flex-col gap-2">
    {(cards ?? []).map((c, i) => {
      if (c.type === "listings") return <Listings key={i} c={c} />;
      if (c.type === "places") return <Places key={i} c={c} />;
      if (c.type === "distance") return <Distance key={i} c={c} />;
      if (c.type === "link") return <div key={i}><LinkCard c={c} onNavigate={onNavigate} /></div>;
      if (c.type === "call") return <CallCard key={i} />;
      return null;
    })}
  </div>
);
